import React from 'react';
import {
  TEXT_FRAME_TEMPLATES,
  TEXT_TEMPLATE_CATEGORIES,
  TextFrameTemplate,
} from '../utils/textFrameTemplates';

interface UGCTextTemplateGalleryProps {
  /** Called with the chosen template id; the editor inserts its overlay(s) at the playhead. */
  onSelect: (templateId: string) => void;
}

/** A single preview card that roughly mirrors how the template's text will look. */
const TemplateCard: React.FC<{ template: TextFrameTemplate; onSelect: (id: string) => void }> = ({
  template,
  onSelect,
}) => {
  const { swatch } = template;
  const textStyle: React.CSSProperties = {
    color: swatch.gradient ? 'transparent' : swatch.color,
    fontStyle: swatch.italic ? 'italic' : undefined,
    WebkitTextStroke: swatch.stroke ? `0.5px ${swatch.stroke}` : undefined,
    background: swatch.gradient ? 'linear-gradient(90deg, #ff7a18, #af002d)' : undefined,
    WebkitBackgroundClip: swatch.gradient ? 'text' : undefined,
    backgroundClip: swatch.gradient ? ('text' as any) : undefined,
    textShadow: '0 1px 4px rgba(0,0,0,0.6)',
    whiteSpace: 'pre-line',
    lineHeight: 1.15,
  };
  return (
    <button
      type="button"
      onClick={() => onSelect(template.id)}
      title={`Add “${template.label}”`}
      className="group relative flex h-24 flex-col items-center justify-center gap-1 overflow-hidden rounded-xl border border-gray-200 p-2 transition-all hover:border-[#2f4941] hover:ring-2 hover:ring-[#2f4941]/30"
      style={{ background: swatch.background ?? 'linear-gradient(160deg,#2b2b2b,#161616)' }}
    >
      <span className="px-1 text-center text-[13px] font-extrabold leading-tight" style={textStyle}>
        {swatch.text}
      </span>
      <span className="absolute bottom-1 left-0 right-0 truncate px-2 text-center text-[9px] font-medium text-white/70">
        {template.label}
      </span>
    </button>
  );
};

/**
 * Gallery of ready-made text-frame templates, grouped by category. Selecting one asks
 * the editor to drop the template's styled overlay(s) onto the canvas at the playhead.
 */
export const UGCTextTemplateGallery: React.FC<UGCTextTemplateGalleryProps> = ({ onSelect }) => {
  return (
    <div className="h-full overflow-y-auto p-5">
      <p className="mb-4 text-[12px] text-gray-500">
        Pick a text design — it drops onto the canvas at the playhead, ready to edit.
      </p>
      <div className="space-y-5">
        {TEXT_TEMPLATE_CATEGORIES.map((cat) => {
          const items = TEXT_FRAME_TEMPLATES.filter((t) => t.category === cat.key);
          if (!items.length) return null;
          return (
            <div key={cat.key}>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {cat.label}
              </h3>
              <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4">
                {items.map((t) => (
                  <TemplateCard key={t.id} template={t} onSelect={onSelect} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UGCTextTemplateGallery;
